import _ from 'lodash';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import axios from 'axios';
import { Card, CardBody, Table, MenuSVG, HeaderTitle } from '../../../../components';
import { FilterQueryParams, getDefaultReportStartDate } from '../../../../helpers';
import { checkFileStatus } from '../../../../helpers/fibonacciHelper';
import { downloadCSV } from '../../../../helpers/download';
import { checkUserPermission, PERMISSIONS } from '../../../../helpers/PermissionsChecker';
import REPORT_TYPES from '../../../../constants/reports/types';
import { useUsersUIContext } from '../context/UsersUIContext';
import * as usersUIHelpers from '../context/UsersUIHelpers';
import page from '../../../../constants/pages/page';
import { usersService, reportsService } from '../../../../services';
import usersColumns from './UsersColumns';
import { CreateUserDialog, DeleteUserDialog, EditUserDialog, RecoverUserDialog } from '../dialogs';
import UsersCustomFilters from './filters/UsersCustomFilters';
import UsersMenuDialog from './UsersMenuDialog';
import { Box } from '@mui/material';
import enabledPages from '../../../../constants/pages/enabledPages';
import { useTranslation } from 'react-i18next';
import CustomAlert from '../../../../components/Alert';

const REPORT_NAME_PREFIX = 'ReporteUsuarios';
const POLLING_TIME_LIMIT_SECONDS = 60;
const POLLING_BACKOFF_MULTIPLIER = 3;
const DEFAULT_USERS_REPORT_TYPE_ID = REPORT_TYPES.users?.id || 58;
const MIN_EXTERNAL_USER_ID = 10000;
const MAX_USER_ID = 1000000000000;
const TOTAL_USERS_PAGE_SIZE = 100000;

function UsersTable ({ user }) {
    const usersFiltersRef = useRef();
    const { t } = useTranslation();
    const canDownload = checkUserPermission(user, PERMISSIONS.DOWNLOAD_LIST);
    // Users to filter
    const userToFilter = ['37352815', '33542808', '38749363'];

    // Context State
    const usersUIContext = useUsersUIContext();
    const usersUIProps = useMemo(() => {
        return {
            isLoading: usersUIContext.isLoading,
            setIsLoading: usersUIContext.setIsLoading,
            queryParams: usersUIContext.queryParams,
            setQueryParams: usersUIContext.setQueryParams
        };
    }, [usersUIContext]);

    // List state
    const [usersList, setUsersList] = useState();

    // Dialogs state
    const [dialog, setDialog] = useState();

    // Alert states
    const [alert, setAlert] = useState({
        severity: '',
        title: '',
        description: '',
        className: ''
    });
    const [openAlert, setOpenAlert] = useState(false);

    const [totalUsers, setTotalUsers] = useState(0);
    // Functions
    const getFilterParams = () => {
        const filters = new FilterQueryParams();
        if (usersUIProps.queryParams.filters) {
            filters.filters = _.cloneDeep(usersUIProps.queryParams.filters.filters);
        }
        filters.between('id', MIN_EXTERNAL_USER_ID, MAX_USER_ID);
        return filters;
    };

    const fetchUsers = () => {
        // change state isLoading
        usersUIProps.setIsLoading(true);

        // Prepare options (order, pages and filters)
        const filters = getFilterParams();

        const { sortField, sortOrder } = usersUIProps.queryParams;
        const options = {
            order: `${usersUIHelpers.sortedKeyMap[sortField] || sortField}-${sortOrder.toUpperCase()}`,
            pageSize: usersUIProps.queryParams.pageSize,
            unionPeople: true,
            page: usersUIProps.queryParams.pageNumber,
            view: 'users'
        };

        // Filter only user for platform backoffice (Web)
        // filters.equal('idPlatform', platforms.backoffice);

        // Set filters on options.
        options.filters = filters.build();

        usersService
            .getUsersCreditCards(options)
            .then(users => {
                // Filter C-TECH users for Production
                const filterUsersCtech = users.filter(user => {
                    if (!enabledPages.enableCtechUsers) {
                        return !(userToFilter.includes(user.document) && user.user_status === 'inactive');
                    }
                    else {
                        return user;
                    }
                });
                // Filter to avoid duplicates
                const uniqueIds = new Set();
                const filterUniqueIds = filterUsersCtech.filter(obj => {
                    if (!uniqueIds.has(obj.id)) {
                        uniqueIds.add(obj.id);
                        return true;
                    }
                    return false;
                });
                setUsersList(filterUniqueIds);
                usersUIProps.setIsLoading(false);
            })
            .catch(e => {
                setUsersList([]);
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'error',
                    title: t('errors.generalTitle'),
                    description: t('errors.generalDescription'),
                    className: 'clientsMC_Alert--error'
                }));
                setOpenAlert(true);
                usersUIProps.setIsLoading(false);
            });
    };

    const getUser = id => usersList && usersList.length ? _.find(usersList, ['id', id]) : null;

    const handleClickMenuUser = (id) => {
        const user = getUser(id);
        setDialog({
            type: 'menu',
            user,
        });
    };

    const getTotalUsers = async () => {
        usersUIProps.setIsLoading(true);
        const filters = getFilterParams();
        const options = {
            unionPeople: true,
            pageSize: TOTAL_USERS_PAGE_SIZE,
            view: 'users'
        };
        options.filters = filters.build();
        await usersService
            .getUsersCreditCards(options)
            .then(users => {
                const filterUsersCtech = users.filter(user => {
                    if (!enabledPages.enableCtechUsers) {
                        return !(userToFilter.includes(user.document) && user.user_status === 'inactive');
                    }
                    else {
                        return user;
                    }
                });
                const uniqueIds = new Set();
                const filterUniqueIds = filterUsersCtech.filter(obj => {
                    if (!uniqueIds.has(obj.id)) {
                        uniqueIds.add(obj.id);
                        return true;
                    }
                    return false;
                });
                setTotalUsers(filterUniqueIds.length);
            })
            .catch(() => {
                setTotalUsers(0);
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'error',
                    title: t('errors.generalTitle'),
                    description: t('errors.generalDescription'),
                    className: 'clientsMC_Alert--error'
                }));
                setOpenAlert(true);
            })
            .finally(() => usersUIProps.setIsLoading(false));
    };

    const replaceCommaPattern = /\[([^,]+),([^,\]]+)\]/;
    const replaceComma = (match, p1, p2) => {
        return `[${p1};${p2}]`;
    };

    const getDownloadFilters = () => {
        if (!JSON.stringify(usersUIProps?.queryParams?.filters?.filters)) { return ''; }
        const filters = { ...usersUIProps?.queryParams?.filters?.filters };
        delete filters.created_at;
        return JSON.stringify(filters)
            .replace(/[{}"]/g, '')
            .replace(/:/g, ' ')
            .replace(replaceCommaPattern, replaceComma);
    };

    const download = async () => {
        usersUIProps.setIsLoading(true);

        const fetchFile = (idFile) => {
            return checkFileStatus({ filters: `id eq ${idFile}` }, POLLING_TIME_LIMIT_SECONDS, POLLING_BACKOFF_MULTIPLIER);
        };
        const startDate = usersUIProps.queryParams?.filters?.filters?.created_at?.be[0] || getDefaultReportStartDate();
        const endDate = usersUIProps.queryParams?.filters?.filters?.created_at?.be[1] || moment().utc().format('YYYY-MM-DD HH:mm:ss');
        const filtersStr =
            (usersUIProps.queryParams?.filters?.filters?.created_at?.be[0]
                ? `created_at be [${startDate};${endDate}]`
                : '') +
            (usersUIProps.queryParams?.filters?.filters?.created_at?.be[0] &&
            getDownloadFilters()
                ? `,${getDownloadFilters()}`
                : getDownloadFilters() || '');

        const options = {
            idType: DEFAULT_USERS_REPORT_TYPE_ID,
            startAt: startDate,
            endAt: endDate,
            metadata: {
                filters: filtersStr,
                order: 'id-DESC',
            }
        };

        const fileName = `${REPORT_NAME_PREFIX}${moment().format('YYYYMMDDHHmmss')}.csv`;

        await reportsService
            .createReport(options)
            .then(async (response) => {
                if (response.metadata && response.metadata.url) {
                    return { dataUrl: response.metadata.url };
                }
                return await fetchFile(response.metadata.id_file);
            })
            .then(async (res) => {
                const url = res.dataUrl || res[0].description;
                return await axios.get(url, { responseType: 'arraybuffer' });
            })
            .then((response) => {
                usersUIProps.setIsLoading(false);
                downloadCSV(response.data, fileName);
            })
            .catch(() => {
                usersUIProps.setIsLoading(false);
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'error',
                    title: t('errors.generalTitle'),
                    description: t('errors.downloadFile'),
                    className: 'clientsMC_Alert--error',
                }));
                setOpenAlert(true);
            });
    };

    const getData = async () => {
        await getTotalUsers();
        fetchUsers();
    };

    const resetFilters = () => {
        if (usersFiltersRef.current) {
            usersFiltersRef.current.reset();
        }
    };

    // Handle query params changes. On any param change, fetch users.
    useEffect(() => {
        usersUIProps.setQueryParams(prev => ({ ...prev, pageSize: localStorage.getItem('sizePerPage') || page.pageSize }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (JSON.stringify(usersUIProps.queryParams.url) !== undefined) {
            getData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usersUIProps.queryParams]);

    // Main Component

    return (
        <>
            {(dialog && dialog.type === 'create') && (
                <CreateUserDialog
                    show={true}
                    onHide={() => {
                        setDialog(null);
                    }}
                    setAlert={setAlert}
                    setOpenAlert={setOpenAlert}
                />
            )}
            {(dialog && dialog.type === 'delete') && (
                <DeleteUserDialog
                    show={true}
                    setAlert={setAlert}
                    setOpenAlert={setOpenAlert}
                    user={dialog.user}
                    onHide={() => {
                        setDialog(null);
                    }}
                />
            )}
            {(dialog && dialog.type === 'edit') && (
                <EditUserDialog
                    show={true}
                    user={dialog.user}
                    setAlert={setAlert}
                    setOpenAlert={setOpenAlert}
                    onHide={() => {
                        setDialog(null);
                    }}
                />
            )}
            {(dialog && dialog.type === 'recover') && (
                <RecoverUserDialog
                    show={true}
                    user={dialog.user}
                    onHide={() => {
                        setDialog(null);
                    }}
                />
            )}
            {(dialog && dialog.type === 'menu') && (
                <UsersMenuDialog
                    dialog={dialog}
                    setDialog={setDialog}
                />
            )}
            <CustomAlert
                openAlert={openAlert}
                setOpenAlert={setOpenAlert}
                alert={alert}
                setAlert={setAlert}
            />

            <Card>
                <Box>
                    <HeaderTitle
                        title={t('users.table.title')}
                        reset={resetFilters}
                        total={totalUsers}
                        icon={(<MenuSVG className='mr-1 mb-0'/>)}
                        download={canDownload ? download : null}
                        UIProps={usersUIProps}
                    />
                    <UsersCustomFilters ref={usersFiltersRef} onAdd={() => setDialog({ type: 'create', user: '' })}/>
                </Box>
                <CardBody>
                    <Table
                        data={usersList}
                        columns={usersColumns({ handleClickMenuUser, setDialog, dialog })}
                        sort={usersUIHelpers.defaultSorted}
                        onChange={usersUIProps.setQueryParams}
                        isLoading={usersUIProps.isLoading}
                        total={totalUsers}
                        pagination={{
                            sizePerPage: usersUIProps.queryParams.pageSize,
                            page: usersUIProps.queryParams.pageNumber
                        }}
                    />
                </CardBody>
            </Card>
        </>
    );
};

const mapStateToProps = ({ auth: { user } }) => ({
    user
});

export default connect(mapStateToProps)(UsersTable);
